import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, FieldType, Popover, pivotData} from 'wc-lib';
import {addSeconds, addYears, format} from 'date-fns';

require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/table.svg');
require('wc-lib/icons/download.svg');
require('wc-lib/icons/graph.svg');
require('wc-lib/icons/infocircle.svg');

@Component({
  template: require('./unserved-energy-events.html'),
  name: 'unserved-energy-events',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class UnservedEnergyEvents extends Vue {
  data: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  chartVisible = true;
  tableVisible = false;
  availablePoeTypes: string[] = [];
  selectedPoeType: string = '';
  availableRegions: string[] = [];
  selectedRegion: string = '';
  availableYears: string [] = [];
  selectedYear: string = '';
  infoText: string = 'This figure shows (for the periods where unserved energy occurred) the range of half-hourly unserved energy outcomes for each region over the MTPASA horizon. The figure shows for each month, the maximum, minimum, median, 1st and 3rd quartile of unserved energy observed across all simulations.';
  runDateTime: string = "";
  runNo: string = "";
  id: string;
  columns: Array<any> = [
    { title: 'Region', field: 'REGIONID'}, 
    { title: 'POE Type', field: 'DEMAND_POE_TYPE' }, 
    { title: 'Period Ending', field: 'PERIOD_ENDING', fieldType: FieldType.DateTime}, 
    { title: 'Min', field: 'USE_EVENT_MIN' }, 
    { title: '1st Quartile', field: 'USE_EVENT_LOWERQUARTILE'}, 
    { title: 'Median', field: 'USE_EVENT_MEDIAN' }, 
    { title: '3rd Quartile', field: 'USE_EVENT_UPPERQUARTILE' }, 
    { title: 'Max', field: 'USE_EVENT_MAX' } 
  ];

  get chartButtonType(): string {
    return this.chartVisible ? 'primary' : 'ghost';
  }

  get tableButtonType(): string {
    return this.tableVisible ? 'primary' : 'ghost';
  }

  chartConfig: any = {
    type: 'serial',
    theme: 'simpleblue',
    valueAxes: [{
      id: 'v1',
      position: 'left',
      title: 'Unserved Energy (MW)',
      labelsEnabled: true,
      minimum: 0
    }],
    graphs: [
      {
        type: 'candlestick',
        balloonText: 'Min: [[USE_EVENT_MIN]]\n1st Quartile: [[USE_EVENT_LOWERQUARTILE]]\nMedian: [[USE_EVENT_MEDIAN]]\n3rd Quartile: [[USE_EVENT_UPPERQUARTILE]]\nMax: [[USE_EVENT_MAX]]',
        highField: 'USE_EVENT_MAX',
        openField: 'USE_EVENT_UPPERQUARTILE',
        valueField: 'USE_EVENT_MEDIAN',
        closeField: 'USE_EVENT_LOWERQUARTILE',
        lowField: 'USE_EVENT_MIN',
        fillAlphas: 0.9,
        columnWidth: 0.4
      },
      {
        type: 'column',
        columnWidth: 0.2,
        valueField: 'USE_EVENT_MAX',
        openField: 'USE_EVENT_MAX',
        lineColor: '#e62e00',
        lineThickness: 3,
        showBalloon: false,
        clustered: false
      }, {
        type: 'column',
        columnWidth: 0.2,
        valueField: 'USE_EVENT_MIN',
        openField: 'USE_EVENT_MIN',
        lineColor: '#e62e00',
        lineThickness: 3,
        showBalloon: false,
        clustered: false
      }, {
        type: 'column',
        columnWidth: 0.4,
        valueField: 'USE_EVENT_MEDIAN',
        openField: 'USE_EVENT_MEDIAN',
        lineColor: '#e62e00',
        lineThickness: 3,
        showBalloon: false,
        clustered: false
      }
    ],
    chartCursor: {
      categoryBalloonEnabled: false,
      cursorAlpha: 0,
      zoomable: false
    },
    categoryAxis: {
      color: '#6F7072',
      gridAlpha: 0.5,
      autoGridCount: true
    },
    categoryField: 'MONTH'
  };

  applyFilter() {
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'DEMAND_POE_TYPE': this.selectedPoeType, 'REGIONID': this.selectedRegion, 'YEAR': this.selectedYear });
    this.data = this.dynamicView.data();
  }

  async created() {
    this.id = await DataManager.addEndPoint('../../api/report/MTPASA_CHART_2b', undefined, undefined, 'MTPASA_CHART_2b', undefined, 'yyyy-MM-ddTHH:mm:ss', ['RUN_DATETIME', 'PERIOD_ENDING']);
    this.dynamicView = DataManager.getDynamicView(this.id, 'unserved-energy');
    this.dynamicView.removeFilters();
    let allData = this.dynamicView.data();
    this.runDateTime = format(allData[0].RUN_DATETIME, 'yyyy-MM-dd HH:mm:ss');
    this.runNo = allData[0].RUN_NO;
    let i = 0;
    let currYear = '';
    for(let idx = 0; idx < allData.length; idx++) {
      let x = allData[idx];
      if (idx-1 > 0) {
        let x2 = allData[idx-1];
        if (x2.DEMAND_POE_TYPE !== x.DEMAND_POE_TYPE) {
          i = 0;
        }
      }
      if(this.availablePoeTypes.indexOf(x.DEMAND_POE_TYPE) === -1) {
        this.availablePoeTypes.push(x.DEMAND_POE_TYPE);
      }
      if(this.availableRegions.indexOf(x.REGIONID) === -1) {
        this.availableRegions.push(x.REGIONID);
      }
      if (i === 0) {
        if (idx+1 < allData.length) {
          let x2 = allData[idx+1];
          //if additional month then just use previous year instead
          if (x2.DEMAND_POE_TYPE === x.DEMAND_POE_TYPE) {
            currYear = format(addYears(x.PERIOD_ENDING, 1), 'yyyy');
          } 
        }
      }
      if(this.availableYears.indexOf(currYear) === -1) {
        this.availableYears.push(currYear);
      }
      x.MONTH = format(addSeconds(x.PERIOD_ENDING, -1), 'MMM yyyy');
      x.YEAR = currYear;
      this.selectedRegion = x.REGIONID;
      this.selectedPoeType = x.DEMAND_POE_TYPE;
      this.selectedYear = currYear;
      
      i++;
      if (i === 12) {
        i = 0;
      }
    }

    this.applyFilter();
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  @Watch('selectedPoeType')
  onPoeTypeChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  @Watch('selectedYear')
  onSelectedYearChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }

  chartClick() {
    this.chartVisible = true;
    this.tableVisible = false;
  }

  tableClick() {
    this.tableVisible = true;
    this.chartVisible = false;
  }

  downloadClick() {
    DownloadHelper.download('MTPASA_CHART_2b', this.data, this.columns);
  }
}



// WEBPACK FOOTER //
// src/views/electricity/nem/mtpasa/unserved-energy-events.ts